import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Space, Input, Select, DatePicker, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { LeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import StandardTable from '../../../components/common/StandardTable';
import type { MatClntIssueRequest, MatClntIssueRequestItem, MatWarehouses, VwMatClntWhInventory } from '../../../types';
import { useGetMatClntIssueRequestItems, useGetMatClntIssueRequests, useGetMatClntWarehouses, useGetMatClntWhInventory } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type RowType = MatClntIssueRequestItem & {
  requester?: string | null;
  requester_name?: string | null;
  status?: MatClntIssueRequest['status'];
  req_date?: string | null;
  description?: string | null;
  requested_wh_name?: string | null;
};

export default function MatClntIssueRequestsItemList() {
  const navigate = useNavigate();

  // Base sources
  const { data: itemsRaw, isLoading, refetch } = useGetMatClntIssueRequestItems() as any;
  const { data: hdrRaw, refetch: refetchHdr } = useGetMatClntIssueRequests() as any;
  const { data: whRaw = [] } = useGetMatClntWarehouses() as any;
  const { data: invData = [] } = useGetMatClntWhInventory() as any;

  const items: MatClntIssueRequestItem[] = useMemo(() => (Array.isArray(itemsRaw) ? itemsRaw : (itemsRaw?.data ?? [])), [itemsRaw]);
  const headers: MatClntIssueRequest[] = useMemo(() => (Array.isArray(hdrRaw) ? hdrRaw : (hdrRaw?.data ?? [])), [hdrRaw]);
  const warehouses: MatWarehouses[] = useMemo(() => (Array.isArray(whRaw) ? whRaw : (whRaw?.data ?? [])), [whRaw]);
  const whMap = useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach(w => { if (w?.id != null) m.set(Number(w.id), String(w.name)); });
    return m;
  }, [warehouses]);

  const invRows: VwMatClntWhInventory[] = Array.isArray(invData) ? invData : (invData?.data ?? []);
  const descMap = useMemo(() => {
    const m = new Map<string, { description?: string | null; unit?: string | null; size_text?: string | null }>();
    (invRows || []).forEach(r => {
      if (!m.has(r.itemcode)) m.set(r.itemcode, { description: r.description, unit: r.unit, size_text: (r as any)?.size_text ?? null });
    });
    return m;
  }, [invRows]);

  

  // Join items with header info + enrichments
  const joined: RowType[] = useMemo(() => {
    const byRef = new Map<string, MatClntIssueRequest>();
    (headers || []).forEach(h => byRef.set(String(h.ref_no), h));
    return (items || []).map(it => {
      const h = byRef.get(String(it.ref_no));
      const info = descMap.get(it.itemcode);
      const requested_wh_name = it.warehouse_id != null ? (whMap.get(Number(it.warehouse_id)) || null) : null;
      const baseDesc = info?.description || it.notes || null;
      const combinedDesc = baseDesc ? `${baseDesc}${info?.size_text ? ` | (${info.size_text})` : ''}` : null;
      return {
        ...it,
        requester: h?.requester ?? null,
        requester_name: h?.requester_name ?? null,
        status: h?.status,
        req_date: h?.req_date ?? null,
        description: combinedDesc,
        requested_wh_name,
      } as RowType;
    });
  }, [items, headers, descMap, whMap]);

  // Filters
  const [refNo, setRefNo] = useState<string>('');
  const [requester, setRequester] = useState<string>('');
  const [requesterName, setRequesterName] = useState<string>('');
  const [status, setStatus] = useState<MatClntIssueRequest['status'] | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [itemcode, setItemcode] = useState<string>('');
  const [warehouse, setWarehouse] = useState<string>('');
  const [descSearch, setDescSearch] = useState<string>('');
  const [debouncedDesc, setDebouncedDesc] = useState<string>('');

  useEffect(() => { const t = setTimeout(() => setDebouncedDesc(descSearch), 300); return () => clearTimeout(t); }, [descSearch]);
  const norm = (s: any) => (s == null ? '' : String(s)).trim().toLowerCase();

  // Base for cascading options (date + status)
  const baseForOptions = useMemo(() => {
    const [start, end] = dateRange || [null, null];
    return (joined || []).filter((r) => {
      if (status && r.status !== status) return false;
      if (start && r.req_date && dayjs(r.req_date).isBefore(start, 'day')) return false;
      if (end && r.req_date && dayjs(r.req_date).isAfter(end, 'day')) return false;
      return true;
    });
  }, [joined, status, dateRange]);

  const refNoOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !requester || String(r.requester) === requester)
      .filter(r => !requesterName || norm(r.requester_name).includes(norm(requesterName)))
      .filter(r => !itemcode || String(r.itemcode) === itemcode)
      .filter(r => !warehouse || String(r.warehouse_id ?? '') === warehouse)
      .forEach(r => { if (r?.ref_no) s.add(String(r.ref_no)); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [baseForOptions, requester, requesterName, itemcode, warehouse]);

  const requesterOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !refNo || String(r.ref_no) === refNo)
      .filter(r => !requesterName || norm(r.requester_name).includes(norm(requesterName)))
      .filter(r => !itemcode || String(r.itemcode) === itemcode)
      .filter(r => !warehouse || String(r.warehouse_id ?? '') === warehouse)
      .forEach(r => { if (r?.requester) s.add(String(r.requester)); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [baseForOptions, refNo, requesterName, itemcode, warehouse]);

  const requesterNameOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !refNo || String(r.ref_no) === refNo)
      .filter(r => !requester || String(r.requester) === requester)
      .filter(r => !itemcode || String(r.itemcode) === itemcode)
      .filter(r => !warehouse || String(r.warehouse_id ?? '') === warehouse)
      .forEach(r => { if (r?.requester_name) s.add(String(r.requester_name)); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [baseForOptions, refNo, requester, itemcode, warehouse]);

  const itemcodeOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !refNo || String(r.ref_no) === refNo)
      .filter(r => !requester || String(r.requester) === requester)
      .filter(r => !requesterName || norm(r.requester_name).includes(norm(requesterName)))
      .filter(r => !warehouse || String(r.warehouse_id ?? '') === warehouse)
      .forEach(r => { if (r?.itemcode) s.add(String(r.itemcode)); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [baseForOptions, refNo, requester, requesterName, warehouse]);

  const warehouseOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !refNo || String(r.ref_no) === refNo)
      .filter(r => !requester || String(r.requester) === requester)
      .filter(r => !requesterName || norm(r.requester_name).includes(norm(requesterName)))
      .filter(r => !itemcode || String(r.itemcode) === itemcode)
      .forEach(r => { if (r?.warehouse_id != null) s.add(String(r.warehouse_id)); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: whMap.get(Number(v)) || v }));
  }, [baseForOptions, refNo, requester, requesterName, itemcode, whMap]);

  // Auto-clear invalid selections
  useEffect(() => { if (refNo && !refNoOptions.some(o => o.value === refNo)) setRefNo(''); }, [refNoOptions, refNo]);
  useEffect(() => { if (requester && !requesterOptions.some(o => o.value === requester)) setRequester(''); }, [requesterOptions, requester]);
  useEffect(() => { if (requesterName && !requesterNameOptions.some(o => o.value === requesterName)) setRequesterName(''); }, [requesterNameOptions, requesterName]);
  useEffect(() => { if (itemcode && !itemcodeOptions.some(o => o.value === itemcode)) setItemcode(''); }, [itemcodeOptions, itemcode]);
  useEffect(() => { if (warehouse && !warehouseOptions.some(o => o.value === warehouse)) setWarehouse(''); }, [warehouseOptions, warehouse]);

  const filtered = useMemo(() => {
    const [start, end] = dateRange || [null, null];
    const descTokens = (debouncedDesc || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
    return (joined || []).filter((r) => {
      if (refNo && String(r.ref_no) !== refNo) return false;
      if (requester && String(r.requester) !== requester) return false;
      if (requesterName && !norm(r.requester_name).includes(norm(requesterName))) return false;
      if (status && r.status !== status) return false;
      if (itemcode && String(r.itemcode) !== itemcode) return false;
      if (warehouse && String(r.warehouse_id ?? '') !== warehouse) return false;
      if (descTokens.length > 0) {
        const hay = norm(r.description);
        if (!descTokens.every(t => hay.includes(t))) return false;
      }
      if (start && r.req_date && dayjs(r.req_date).isBefore(start, 'day')) return false;
      if (end && r.req_date && dayjs(r.req_date).isAfter(end, 'day')) return false;
      return true;
    });
  }, [joined, refNo, requester, requesterName, status, itemcode, warehouse, dateRange, debouncedDesc]);

  const columns: ColumnsType<RowType> = useMemo(() => [
    { title: 'Ref No', dataIndex: 'ref_no', key: 'ref_no', width: 140, sorter: (a, b) => String(a.ref_no).localeCompare(String(b.ref_no)) },
    { title: 'Req Date', dataIndex: 'req_date', key: 'req_date', width: 120, sorter: (a, b) => dayjs(a.req_date || 0).unix() - dayjs(b.req_date || 0).unix(), render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 140, sorter: (a, b) => String(a.status || '').localeCompare(String(b.status || '')), render: (v: any) => <Tag color={
      v === 'pending' ? 'gold' : v === 'approved' ? 'blue' : v === 'partially_issued' ? 'purple' : v === 'issued' ? 'green' : v === 'rejected' ? 'red' : 'default'
    }>{v}</Tag> },
    { title: 'Requester', dataIndex: 'requester', key: 'requester', width: 120, sorter: (a, b) => String(a.requester || '').localeCompare(String(b.requester || '')) },
    { title: 'Requester Name', dataIndex: 'requester_name', key: 'requester_name', width: 180, sorter: (a, b) => String(a.requester_name || '').localeCompare(String(b.requester_name || '')) },
    { title: 'Itemcode', dataIndex: 'itemcode', key: 'itemcode', width: 140, sorter: (a, b) => String(a.itemcode || '').localeCompare(String(b.itemcode || '')) },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true, sorter: (a, b) => String(a.description || '').localeCompare(String(b.description || '')) },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 80, sorter: (a, b) => String(a.unit || '').localeCompare(String(b.unit || '')) },
    { title: 'Warehouse', dataIndex: 'requested_wh_name', key: 'requested_wh_name', width: 200,
      sorter: (a, b) => String((a.requested_wh_name ?? a.warehouse_id ?? '')).localeCompare(String((b.requested_wh_name ?? b.warehouse_id ?? ''))),
      render: (_, r) => r.requested_wh_name ?? (r.warehouse_id != null ? (whMap.get(Number(r.warehouse_id)) || r.warehouse_id) : '-') },
    { title: 'Requested', dataIndex: 'requested_qty', key: 'requested_qty', width: 120, align: 'right', sorter: (a, b) => (a.requested_qty || 0) - (b.requested_qty || 0) },
    { title: 'Item Notes', dataIndex: 'notes', key: 'notes', ellipsis: true, sorter: (a, b) => String(a.notes || '').localeCompare(String(b.notes || '')) },
  ], [whMap]);

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Client Issue Request Items</Title>
              <Text type="secondary">Itemized list of all client request lines</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Select showSearch allowClear placeholder="Ref No" value={refNo || undefined} onChange={(v) => setRefNo(v || '')} options={refNoOptions} style={{ width: 140 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())} />
            <Select showSearch allowClear placeholder="Requester (Code)" value={requester || undefined} onChange={(v) => setRequester(v || '')} options={requesterOptions} style={{ width: 140 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())} />
            <Select showSearch allowClear placeholder="Requester Name" value={requesterName || undefined} onChange={(v) => setRequesterName(v || '')} options={requesterNameOptions} style={{ width: 180 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())} />
            <Select placeholder="Status" allowClear value={status} onChange={(v) => setStatus(v)} style={{ width: 130 }} options={["pending","approved","partially_issued","issued","rejected","cancelled"].map(s => ({ value: s, label: s }))} />
            <Select showSearch allowClear placeholder="Itemcode" value={itemcode || undefined} onChange={(v) => setItemcode(v || '')} options={itemcodeOptions} style={{ width: 140 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())} />
            <Select showSearch allowClear placeholder="Warehouse" value={warehouse || undefined} onChange={(v) => setWarehouse(v || '')} options={warehouseOptions} style={{ width: 260 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())} />
            <Input placeholder="Description contains..." allowClear value={descSearch} onChange={(e) => setDescSearch(e.target.value)} style={{ width: 220 }} />
            <RangePicker size="small" value={dateRange as any} onChange={(v) => setDateRange(v as any)} style={{ width: 220 }} />
            <Button size="small" icon={<ReloadOutlined />} onClick={() => refetch?.()}>Refresh</Button>
            <Button size="small" onClick={() => { setRefNo(''); setRequester(''); setRequesterName(''); setStatus(undefined); setDateRange(null); setItemcode(''); setWarehouse(''); setDescSearch(''); }}>Reset</Button>
          </Space>
        </Col>
      </Row>

      <StandardTable<RowType>
        columns={columns}
        dataSource={filtered}
        rowKey={(r) => `${r.ref_no}-${r.id}`}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
